import React, {PropsWithChildren} from 'react';
import ReactDOM from 'react-dom';
import {
    iterableAny,
    iterableMapToArray,
    objectGetKeys,
    objectShallowEqual,
    stringIsNotNullableOrWhiteSpace
} from "@wix/devzai-utils-common";

// Type guard for React 18 APIs
interface ReactDOM18 {
  createRoot: (container: Element | DocumentFragment) => {
    render: (children: React.ReactElement) => void;
    unmount: () => void;
  };
  hydrateRoot: (
    container: Element | DocumentFragment, 
    children: React.ReactElement
  ) => void;
}

export type ReactComponentProps<T> = T extends React.ComponentType<infer P> ? PropsWithChildren<P> : never;

export function reactCreateMemo<T extends React.ComponentType<any>>(
    Comp: T,
    propsAreEqual?: (prevProps: Readonly<ReactComponentProps<T>>, nextProps: Readonly<ReactComponentProps<T>>) => boolean
) : T {
    return (React.memo(Comp, propsAreEqual) as unknown) as T;
}

export function reactForwardRef<COMP extends React.FC<any>>(
    comp: COMP
) : COMP {
    return React.forwardRef((props: any, ref) => {
        return comp({
            ...props,
            ref: ref
        })
    }) as unknown as COMP;
}

// export function reactForwardRef<COMP extends ForwardRefExoticComponent<any>>(
//     render: COMP extends ForwardRefExoticComponent<PropsWithoutRef<infer P> & RefAttributes<infer T>> ?
//         ForwardRefRenderFunction<T, P> : never
// ) : COMP {
//     return (React.forwardRef(render) as unknown) as COMP;
// }

// export function reactForwardRef<RENDER extends ForwardRefRenderFunction<any, any>>(
//     render: RENDER
// ) : COMP {
//     return (React.forwardRef(render) as unknown) as COMP;
// }

export function reactIsRefObject<R>(value: unknown) : value is React.RefObject<R> {
    return (value as React.RefObject<R>).current !== undefined;
}

export function reactWrapNode<T extends React.ReactNode, R extends React.ReactNode> (reactNode: T, wrappingFunc: (reactNode: T) => R) {
    return wrappingFunc(reactNode);
}

export function reactProvideContextValue<T> (reactNode: React.ReactNode, context: React.Context<T>, value: T) {
    const ContextProvider = context.Provider;

    return (
        <ContextProvider value={value}>
            {reactNode}
        </ContextProvider>
    )
}

class ReactMultipleContextValuesProvider {

    private contextValues: {context: React.Context<any>, value: any}[] = [];

    public addContextValue<T> (context: React.Context<T>, value: T, isEnabled = true) {
        if (!isEnabled) {
            return this;
        }

        this.contextValues.push({context: context, value: value});

        return this;
    }

    public render (reactNode: React.ReactElement) {
        return this.contextValues.reduce((acc, {context, value}) => {
            return reactProvideContextValue(acc, context, value);
        }, reactNode);
    }
}

export function reactProvideContextValues () {
    return new ReactMultipleContextValuesProvider();
}

export function reactSetInnerHtml (html: string) {
    return {
        [['dangerously', 'SetInnerHTML'].join('')]: {
            __html: html
        }
    }
}

export function reactCreatePropsComparisonWithInspection<P> () {
    return (prevProps: Readonly<PropsWithChildren<P>>, nextProps: Readonly<PropsWithChildren<P>>) => {

        const prevPropsKeys = objectGetKeys(prevProps);
        const propsDiff = iterableMapToArray(prevPropsKeys, (key, skip) => {
            const prevPropValue = prevProps[key];
            const nextPropValue = nextProps[key];

            return prevPropValue !== nextPropValue ? {
                key: key,
                prevPropValue: prevPropValue,
                nextPropValue: nextPropValue
            } : skip
        })

        if (propsDiff.length > 0) {
            console.dir(propsDiff);
        }

        return objectShallowEqual(prevProps, nextProps);
    }
}

export function reactIsEmptyNode (reactNode: React.ReactNode) {
    return !reactIsNotEmptyNode(reactNode);
}

export function reactIsNotEmptyNode (reactNode: React.ReactNode) {
    return typeof reactNode === 'string' ?
        stringIsNotNullableOrWhiteSpace(reactNode) :
        reactNode !== null && reactNode !== undefined
}

export function reactAreSomeNodesNotEmpty (...reactNode: React.ReactNode[]) {
    return iterableAny(reactNode, reactIsNotEmptyNode);
}

export function reactIsValidReactElement<P = any> (value: unknown) : value is React.ReactElement<P> {
    return React.isValidElement<P>(value)
}

/**
 * Renders a React application into a container, using the appropriate API
 * for the loaded React version (React 16 render() vs React 18 createRoot()).
 */
export function reactRender(
  container: Element | DocumentFragment, 
  app: React.ReactElement
): void {
  const isReact18 = 'createRoot' in ReactDOM;

  if (isReact18) {
    const ReactDOM18 = ReactDOM as unknown as ReactDOM18;
    ReactDOM18.createRoot(container).render(app);
  } else {
    ReactDOM.render(app, container);
  }
}

/**
 * Hydrates a server-rendered React application, using the appropriate API
 * for the loaded React version (React 16 hydrate() vs React 18 hydrateRoot()).
 */
export function reactHydrate(
  container: Element | DocumentFragment, 
  app: React.ReactElement
): void {
  const isReact18 = 'createRoot' in ReactDOM;

  if (isReact18) {
    const ReactDOM18 = ReactDOM as unknown as ReactDOM18;
    ReactDOM18.hydrateRoot(container, app);
  } else {
    ReactDOM.hydrate(app, container);
  }
}

/**
 * Creates a React root with render() and unmount() methods, using the appropriate API
 * for the loaded React version (React 16 render/unmount vs React 18 createRoot).
 */
export function reactCreateRoot(container: Element | DocumentFragment) {
  const isReact18 = 'createRoot' in ReactDOM;

  if (isReact18) {
    const ReactDOM18 = ReactDOM as unknown as ReactDOM18;
    return ReactDOM18.createRoot(container);
  } else {
    // Return a React 16 compatible interface
    return {
      render: (app: React.ReactElement) => {
        ReactDOM.render(app, container);
      },
      unmount: () => {
        ReactDOM.unmountComponentAtNode(container);
      }
    };
  }
}


/**
 * Flushes React state updates synchronously if available (React 18+).
 * Falls back to just calling the callback in React 16 where setState in event handlers
 * is already synchronous by default.
 * 
 * Use this when you need to ensure pending state updates are processed before
 * performing DOM operations like animations that might conflict with React re-renders.
 */
export function reactFlushSyncIfAvailable<T>(callback: () => T): T {
  const flushSync = (ReactDOM as any).flushSync;
  if (typeof flushSync === 'function') {
    return flushSync(callback);
  }
  // React 16 fallback - just call the callback directly
  return callback();
}


/**
 * A delegate function that allows customizing how a component is rendered.
 * The delegate receives a render function and decides what overrides to pass.
 */
export type ReactDelegatedComponentRender<TComponentProps> = (
    render: (overrides: Partial<TComponentProps>) => React.ReactElement
) => React.ReactElement;

export function reactRenderDelegatedComponent<TComponentProps>(
    delegate: ReactDelegatedComponentRender<TComponentProps>,
    render: (overrides: Partial<TComponentProps>) => React.ReactElement
): React.ReactElement {
    return delegate(render);
}

export function reactStateSetIfChanged<T>(
    setState: React.Dispatch<React.SetStateAction<T>>,
    newValue: NoInfer<T>,
    equalityComparer: (prev: NoInfer<T>, next: NoInfer<T>) => boolean
) {
    setState(prev => equalityComparer(prev, newValue) ? prev : newValue);
}